"use client";

import "@/app/style/raceHeader.scss";
import CalendarIconButton from "@/app/ui/RaceCalendar/components/RaceCalendar/calendarButton";
import { getAdditionalInfoForFixture, getSearchParams, theme } from "@/app/utils/Common";
import dayJS, { dateFormat, formattedDate, toDate } from "@/app/utils/DayJS";
import { LANGTYPE_EN, useLanguage } from '@/app/utils/LanguageContext';
import { textSize } from "@/app/utils/MixinSize";
import { useMyContext } from '@/app/utils/MyContext';
import { getBusinessDate, getFixtureList } from '@/app/utils/request/requestList';
import { Popper } from '@mui/base';
import { Grid, useMediaQuery } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState, useRef } from 'react';
import RacingCalendar from '../RaceCalendar/components/RaceCalendar/racingCalendar';

interface PropType {
    meetings?: IMeeting[];
    anchorEl?: null | HTMLElement;
    isDisplay?: boolean;
    type?: "pc" | "mobile";
    handleCalendarClose?: () => void;
}

const CalendarPopper = (props: PropType) => {
    const { state: myContextState, dispatch: myContextDispatch } = useMyContext();
    const { state: lang } = useLanguage();
    const { isDisplay, anchorEl, meetings, type, handleCalendarClose } = props;
    const { t } = lang;
    const isMobile = type !== "pc";
    const [displayMonth, setDisplayMonth] = useState<Dayjs>();
    const [fixtureData, setFixtureData] = useState<{ fixtureList: IFixture[]; fixtureConfig: any }>();
    const [businessDate, setBusinessDate] = useState<Dayjs>();
    const [isCurrentMonthFin, setIsCurrentMonthFin] = useState<boolean>(false);
    const arrowRef = useRef<HTMLDivElement | null>(null);

    const { RaceDate: urlDate } = getSearchParams(new URLSearchParams(window.location.search));

    const serverDateFormat = lang.langType === LANGTYPE_EN ? "M/D/YYYY" : "D/M/YYYY";

    const setInitialMonthDisplay = (fixtureList: IFixture[], businessDate: Dayjs) => {
        let targetDate = dayJS(urlDate, "YYYYMMDD").isValid() ? dayJS(urlDate, "YYYYMMDD") : businessDate; // hightlight date in url 

        if (!fixtureList.some((r: any) => dayJS(r.date_HK?.value, serverDateFormat).isSame(businessDate, 'month') && dayJS(r.date_HK?.value, serverDateFormat).isSameOrAfter(businessDate))) {
            setIsCurrentMonthFin(true);
        }

        if (!fixtureList.some((r: any) => dayJS(r.date_HK?.value, serverDateFormat).isSame(targetDate, 'month') && dayJS(r.date_HK?.value, serverDateFormat).isSameOrAfter(targetDate))) {
            // current month does not have upcoming meeting
            if (fixtureList.some((r: any) => dayJS(r.date_HK?.value, serverDateFormat).isSame(targetDate?.add(1, 'month'), 'month'))) {
                setDisplayMonth(targetDate?.add(1, 'month'));
                return
            }
        }

        setDisplayMonth(targetDate);
    }

    const fetchFixtureData = useMemo(() => async (businessDate: Dayjs) => {
        getFixtureList(lang.langCode, true)
            .then((res: any) => {
                let newList = res?.data?.fixtureList?.filter((f: IFixture) => dayJS(f.date_HK?.value, serverDateFormat).isSameOrAfter(dayJS(window.globalConfig?.OTHERS?.CUTOFF_DATE, "YYYYMMDD")));
                newList = newList?.map((f: IFixture) => {
                    // add raceDatetime from meeting level to fixture list by meeting mapping field
                    const additData = getAdditionalInfoForFixture(lang.langCode, f, "_Calendar");
                    const newObj = {
                        ...f,
                        ...additData,
                    };

                    return newObj
                });

                setInitialMonthDisplay(newList, businessDate);
                setFixtureData({
                    fixtureList: newList,
                    fixtureConfig: res?.config
                })
            })
            .catch((e: any) => {
                console.log(e);
            });
    }, []);

    useEffect(() => {
        getBusinessDate()
            .then((res: any) => {
                const bizdate: string = res?.timeOffset?.ts?.substring(0, res?.timeOffset?.ts?.indexOf('+'));
                setBusinessDate(dayJS(bizdate, "YYYY-MM-DD"));
                fetchFixtureData(dayJS(bizdate, "YYYY-MM-DD"));
            })
            .catch((e: any) => {
                console.log(e);
            });
    }, [])

    const id = props?.isDisplay ? 'simple-popper' : undefined
    return (
        isMobile ?
            <>
                <Drawer
                    anchor="bottom"
                    open={Boolean(anchorEl)}
                    onClose={handleCalendarClose}
                    PaperProps={{
                        sx: {
                            height: '94%',
                            borderRadius: "12px 12px 0 0"
                        },
                    }}
                >
                    <Grid container item lg={12} sx={{ borderBottom: "1px solid #B4B4B4", margin: "0 10px" }} className="calendarDrawerHeader">
                        <Grid item sx={{ ...textSize(15), marginInline: "auto"}}>
                            <span>{t("racingCalendar")}</span>
                        </Grid>
                        <Box className="drawerCloseBtn" onClick={handleCalendarClose} >
                            <img src={`${myContextState.assetPrefix}/commonImg/close_black.svg`} />
                        </Box>
                    </Grid>

                    <Grid item lg={12} sx={{ borderBottom: "1px solid #B4B4B4", padding: "8px 0", margin: "0 12px" }}>
                        <Grid className="calendarPopperHeader" sx={{ ...textSize(17), justifyContent: "flex-start" }}>
                            <span id="raceHeaderMeetingTitle">{formattedDate(toDate(myContextState?.selectedMeeting?.meetingDate?.value, dateFormat.cms.datetime), dateFormat.display.date)} - {myContextState?.selectedMeeting?.meetingCountry}</span>
                        </Grid>
                    </Grid>
                    <Grid item lg={12}>
                        <RacingCalendar handleCalendarClose={handleCalendarClose} isMobile={true} isSummary isCalendarShown={true} fixtureList={fixtureData?.fixtureList} config={fixtureData?.fixtureConfig} controlledMonth={displayMonth} businessDate={businessDate} isCurMonthEmpty={isCurrentMonthFin} />
                    </Grid>
                </Drawer>
            </>
            :
            <>
                <Popper id={id} open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-end" className="calendarPopper"
                    modifiers={[
                        {
                            name: 'arrow',
                            enabled: true
                        }, {
                            name: 'offset',
                            options: {
                                offset: [0, 20],
                            },
                        }, {
                            name: 'preventOverflow',
                            options: {
                                padding: 16,
                            },
                        },
                    ]}
                    style={{
                        width: '1064px',
                        maxWidth: 'calc(100vw - 32px)',
                    }}
                >
                    <div className="popperContent" >
                        <div data-popper-arrow><div className="popperArrow" ref={arrowRef} /></div>
                        {/* existing content */}
                    </div>
                    <Grid container spacing={0} className="calendarWrapper">
                        <Grid item lg={12} sx={{ borderBottom: "1px solid #B4B4B4", margin: "0 10px", padding: "8px 12px" }}>
                            <Grid className="calendarPopperHeader" sx={{ ...textSize(17), justifyContent: "flex-start" }}>
                                <span id="raceHeaderMeetingTitle">{formattedDate(toDate(myContextState?.selectedMeeting?.meetingDate?.value, dateFormat.cms.datetime), dateFormat.display.date)} - {myContextState?.selectedMeeting?.meetingCountry}</span>
                            </Grid>
                        </Grid>
                        <Grid item lg={12}>
                            <RacingCalendar handleCalendarClose={handleCalendarClose} isMobile={false} isSummary isCalendarShown={true} fixtureList={fixtureData?.fixtureList} config={fixtureData?.fixtureConfig} controlledMonth={displayMonth} businessDate={businessDate} isCurMonthEmpty={isCurrentMonthFin} />
                        </Grid>
                    </Grid>
                </Popper>
            </>
    )
}

export default CalendarPopper